import React, { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { KPICard } from "../components/KPICard";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Send, Loader2, Clock, RefreshCw } from "lucide-react";

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [expiring, setExpiring] = useState([]);
  const [runningReminders, setRunningReminders] = useState(false);
  const [sendingId, setSendingId] = useState(null);

  async function loadAll() {
    try {
      const [s, e] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/expiring-sellers"),
      ]);
      setStats(s.data);
      setExpiring(e.data);
    } catch {}
  }

  useEffect(() => {
    loadAll();
  }, []);

  async function runReminders() {
    setRunningReminders(true);
    try {
      const { data } = await api.post("/admin/reminders/run");
      toast.success(`Sent ${data.total_sent} reminder${data.total_sent === 1 ? "" : "s"}.`);
      loadAll();
    } catch (e) {
      toast.error(errMsg(e, "Failed"));
    } finally {
      setRunningReminders(false);
    }
  }

  async function sendOne(id) {
    setSendingId(id);
    try {
      const { data } = await api.post(`/admin/sellers/${id}/send-reminder`);
      if (data.status === "sent") toast.success("Reminder sent");
      else if (data.status === "skipped") toast.warning("Email disabled (no Resend key)");
      else toast.error(data.error || "Failed");
      loadAll();
    } catch (e) {
      toast.error(errMsg(e, "Failed"));
    } finally {
      setSendingId(null);
    }
  }

  return (
    <AppShell role="admin">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#4F46E5] font-bold">Admin Control Room</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-2">Operations overview.</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KPICard label="Total Sellers" value={stats?.total_sellers ?? "—"} accent testId="stat-total-sellers" />
        <KPICard label="Active" value={stats?.active ?? "—"} hint={<span className="text-green-600 font-bold">Healthy</span>} testId="stat-active" />
        <KPICard label="Expired" value={stats?.expired ?? "—"} testId="stat-expired" />
        <KPICard label="Disabled" value={stats?.disabled ?? "—"} testId="stat-disabled" />
        <KPICard label="Reports" value={stats?.total_reports ?? "—"} testId="stat-reports" />
        <KPICard label="Uploads" value={stats?.total_uploads ?? "—"} testId="stat-uploads" />
      </div>

      {/* Expiring sellers */}
      <div className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-6 text-white mb-6" data-testid="expiring-sellers">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-[#4F46E5]" />
            <div className="text-sm font-bold uppercase tracking-[0.2em]">Expiring soon (≤ 7 days)</div>
          </div>
          <button
            onClick={runReminders}
            disabled={runningReminders}
            className="btn-primary text-xs disabled:opacity-60"
            data-testid="run-reminders-btn"
          >
            {runningReminders ? <Loader2 size={12} className="inline animate-spin" /> : <><RefreshCw size={12} className="inline mr-1" /> Run 3d / 1d reminders</>}
          </button>
        </div>
        {expiring.length === 0 && (
          <div className="text-sm text-slate-500">No sellers expiring in the next 7 days. You're good.</div>
        )}
        {expiring.length > 0 && (
          <div className="space-y-2">
            {expiring.map((s) => (
              <div
                key={s.id}
                className={`flex items-center justify-between p-3 rounded-md ${
                  s.days_left <= 1 ? "bg-red-50" : s.days_left <= 3 ? "bg-orange-50" : "bg-white/[0.02]"
                }`}
                data-testid={`expiring-row-${s.id}`}
              >
                <div className="flex items-center gap-3">
                  <Clock size={14} className={s.days_left <= 1 ? "text-[#4F46E5]" : "text-slate-500"} />
                  <div>
                    <div className="text-sm font-bold">{s.name}</div>
                    <div className="text-[11px] text-slate-500 font-mono-data">{s.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-bold uppercase tracking-widest ${s.days_left <= 1 ? "text-[#4F46E5]" : "text-slate-300"}`}>
                    {s.days_left <= 0 ? "Today" : `${s.days_left}d`}
                  </span>
                  <button
                    onClick={() => sendOne(s.id)}
                    disabled={sendingId === s.id}
                    className="px-3 py-1.5 border border-white/[0.08] rounded-md text-xs font-bold hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors flex items-center gap-1.5 disabled:opacity-60"
                    data-testid={`send-reminder-${s.id}`}
                  >
                    {sendingId === s.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                    Remind
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-bold uppercase tracking-[0.2em]">Manage Sellers</div>
            <div className="text-sm text-slate-400 mt-1">Create, delete, reset password, renew subscriptions.</div>
          </div>
          <Link to="/admin/sellers" className="btn-primary text-sm" data-testid="go-to-sellers">
            Open Sellers →
          </Link>
        </div>
      </div>
    </AppShell>
  );
}
